// Documents as the backoffice reads them. The domain contract lives in
// @taller/shared (zod); here we add the document id and the server-written
// fields that are not part of the client contract (correlativo, PDF, review,
// annulment, verification...). Server fields are optional on purpose: a
// document may be read before the Functions finish processing it.
import type {
  BajaTaller,
  ConfigGeneral,
  Discrepancia,
  EstadoZona,
  Inspeccion,
  Orden,
  Rol,
  SeguimientoOrden,
} from '@taller/shared';

export type MarcaTiempo = { seconds: number; nanoseconds: number; toDate?: () => Date };

/**
 * A stored instant: a Firestore Timestamp (server-written fields, orders) or
 * an ISO 8601 string (fields the tablet writes offline, e.g. inspection
 * `creadaEn`/`firmadaEn`, see @taller/shared).
 */
export type Instante = MarcaTiempo | string;

export type ConId<T> = T & { id: string };

export type OrdenDoc = ConId<Orden> & {
  cerradaEn?: MarcaTiempo | null;
  /** Customer tracking link (F1), written only by the server. */
  seguimiento?: SeguimientoOrden;
};

export type Revision = {
  por: string;
  porNombre?: string;
  resultado: 'aprobada' | 'observada';
  nota?: string;
  en: MarcaTiempo | null;
};

export type Anulacion = { por: string; porNombre?: string; motivo: string; en: MarcaTiempo | null };

export type InspeccionDoc = ConId<
  // creadaEn / firmadaEn come from @taller/shared as ISO strings written by
  // the tablet (creadaEn in UTC "...Z" so it sorts as a string). Do not
  // redeclare them as Timestamps: Firestore never compares across types.
  Inspeccion & {
    creadaPorNombre?: string;
    numeroCorrelativo?: number;
    pdfPath?: string;
    discrepancias?: Discrepancia[];
    revision?: Revision;
    anulacion?: Anulacion;
    validacion?: Validacion;
    /** On a siniestro: the first Ingreso signed from it (server-written). */
    ingresoId?: string;
  }
>;

/**
 * Server verdict on a signed/closed inspection (onInspeccionFirmada).
 * `errores` reject it (ok: false, no number or PDF). `advertencias` do not:
 * the inspection is processed (ok: true) but something was skipped, e.g.
 * `referencia_corrupta` when the reference could not be compared.
 */
export type Validacion = {
  ok: boolean;
  errores?: { codigo: string; mensaje: string; zona?: string }[];
  advertencias?: { codigo: string; mensaje: string }[];
};

export type EstadoHash = 'verificada' | 'alterada' | 'no_declarada' | 'pendiente';

/** inspecciones/{id}/fotos/{fotoId}: written by onFotoSubida. */
export type VerificacionFoto = {
  id: string;
  estado: 'verificada' | 'alterada' | 'no_declarada';
  sha256Servidor?: string;
  sha256Declarado?: string | null;
  /** Hash frozen at signature time (set when the PDF is generated). */
  sha256Firmado?: string | null;
  bytes?: number;
  contentType?: string | null;
  ruta?: string;
  /** Sealed ~1200px derivative (plate, zone, time, short hash). */
  derivado?: string | null;
  subidaEn?: MarcaTiempo | null;
  verificadaEn?: MarcaTiempo | null;
};

export type UsuarioDoc = {
  id: string;
  nombre: string;
  rol: Rol;
  activo: boolean;
  tienePin: boolean;
  rut?: string;
  /** Required for every person since shared devices; older docs may lack it. */
  email?: string;
  /** Still using the temporary password given by an admin. */
  debeCambiarPassword?: boolean;
  /** Welcome e-mails of the last day (ms), written by crearUsuario/reenviarBienvenida. */
  bienvenidaEnvios?: number[];
  bloqueadoHasta?: MarcaTiempo | null;
};

/** Shared device account (a workshop tablet), talleres/{t}/equipos/{uid}. */
export type EquipoDoc = {
  id: string;
  nombre: string;
  email: string;
  activo: boolean;
  creadoEn?: MarcaTiempo | null;
};

export type DispositivoDoc = {
  id: string;
  nombre: string;
  activo: boolean;
  /** Shared account that enrolled it. */
  equipoId?: string;
  modelo?: string;
  plataforma?: string;
  /** Last person who signed in with a PIN. */
  ultimoUid?: string;
  enroladoPor?: string;
  enroladoPorNombre?: string;
  enroladoEn?: MarcaTiempo | null;
  ultimoUso?: MarcaTiempo | null;
  revocadoEn?: MarcaTiempo | null;
  /** Enrolled by the shared account, waiting for an admin (activo: false). */
  pendiente?: boolean;
  aprobadoPor?: string;
  aprobadoEn?: MarcaTiempo | null;
  /** uids that signed in with a PIN on this tablet (revoked with it). */
  sesiones?: string[];
};

export type Canal = 'whatsapp' | 'correo' | 'sms';

export type AvisoDoc = {
  id: string;
  ordenId?: string;
  /** Set by "Enviar al cliente": ties the notice to that inspection's PDF. */
  inspeccionId?: string;
  patente: string;
  canal: Canal;
  plantilla: string;
  mensaje: string;
  destino: string;
  por: string;
  porNombre?: string;
  en: MarcaTiempo | null;
};

export type MovimientoDoc = {
  id: string;
  /** Stamped by the Functions (older seed data may lack them). */
  tallerId?: string;
  patente?: string;
  tipo: 'avanzar' | 'volver' | 'forzar' | 'terminar' | 'ingreso' | 'auto';
  de: number | null;
  a: number | null;
  aZonaNeutral: boolean;
  motivo?: string;
  por?: string;
  porNombre?: string;
  en: MarcaTiempo | null;
};

export type AuditoriaDoc = {
  id: string;
  accion: string;
  por?: string;
  porNombre?: string;
  en: MarcaTiempo | null;
  detalle?: Record<string, unknown>;
  objetivo?: string;
  resultado?: string;
};

/** vehiculos/{patente}: last known data, updated by the tablet on each reception. */
export type VehiculoDoc = {
  id: string;
  patente: string;
  marca?: string;
  modelo?: string;
  anio?: number;
  color?: string;
  cliente?: { nombre: string; rut?: string; telefono?: string; correo?: string };
  /** ISO string from the tablet; Timestamp in older seed data. */
  actualizadoEn?: Instante | null;
};

export type TallerDoc = {
  id: string;
  nombre: string;
  creadoEn?: MarcaTiempo | null;
  /** false = dado de baja (ficha comercial 'terminado'): every account blocked. */
  activo?: boolean;
  /** true = suspended (ficha comercial 'suspendido'): read-only, nothing is written. */
  soloLectura?: boolean;
  suspension?: { en: MarcaTiempo | null; por: string };
  /** While dado de baja: when, by whom and what was active (bajaTaller.ts). */
  baja?: BajaTaller<MarcaTiempo | null>;
  /** Last Terms version accepted (aceptarTerminos; shared/src/terminos.ts). */
  terminos?: { version: string; aceptadoEn: MarcaTiempo | null; aceptadoPor: string };
};

export type Fase = ConfigGeneral['fases'][number];

export type { ConfigGeneral, EstadoZona };

/** Millis of a Timestamp or ISO string; null when missing or unparseable. */
export function aMillis(t: Instante | null | undefined): number | null {
  if (!t) return null;
  if (typeof t === 'string') {
    const ms = Date.parse(t);
    return Number.isNaN(ms) ? null : ms;
  }
  return t.seconds * 1000 + Math.floor(t.nanoseconds / 1e6);
}

/**
 * Lower bound for a range query on inspection `creadaEn`: the same UTC ISO
 * format the tablet writes ("2026-09-24T03:00:00.000Z"), so Firestore compares
 * string to string and lexicographic order matches time order.
 */
export function cotaIso(ms: number): string {
  return new Date(ms).toISOString();
}

/** The `where` of the inspection list's date range (kept pure to test it). */
export function filtroCreadaDesde(desde: number): ['creadaEn', '>=', string] {
  return ['creadaEn', '>=', cotaIso(desde)];
}

export function aFecha(t: Instante | null | undefined): Date | null {
  const ms = aMillis(t);
  return ms === null ? null : new Date(ms);
}

/** Best timestamp for an inspection: server creation, else signature time. */
export function fechaInspeccion(i: InspeccionDoc): Date | null {
  return (
    aFecha(i.creadaEn) ??
    aFecha(i.firmadaEn) ??
    (i.firma?.en ? new Date(i.firma.en) : null) ??
    (i.firmaRetiro?.en ? new Date(i.firmaRetiro.en) : null)
  );
}
